/**
 * Button text constants for consistent UI messaging
 */
export const BUTTON_TEXT = {
    OK: 'OK',
    CANCEL: 'Cancel',
    CONFIRM: 'Confirm',
    SUBMIT: 'Submit',
    SAVE: 'Save',
    DELETE: 'Delete',
    EDIT: 'Edit',
    CLOSE: 'Close',
    BACK: 'Back',
    NEXT: 'Next',
    PREVIOUS: 'Previous',
    RETRY: 'Retry',
    CONTINUE: 'Continue',
    FINISH: 'Finish',
    RESET: 'Reset',
    CLEAR: 'Clear',
    APPLY: 'Apply',
    REJECT: 'Reject',
    APPROVE: 'Approve',
    ACCEPT: 'Accept',
    DECLINE: 'Decline',
    YES: 'Yes',
    NO: 'No',

    // Dialog specific buttons
    DIALOG: {
        OK: 'OK',
        CANCEL: 'Cancel',
        CONFIRM: 'Confirm',
        CLOSE: 'Close',
        YES: 'Yes',
        NO: 'No',
        CONTINUE: 'Continue',
        RETRY: 'Retry',
    },

    // Form specific buttons
    FORM: {
        SUBMIT: 'Submit',
        SAVE: 'Save',
        CANCEL: 'Cancel',
        RESET: 'Reset',
        CLEAR: 'Clear',
        APPLY: 'Apply',
    },

    // Action specific buttons
    ACTIONS: {
        EDIT: 'Edit',
        DELETE: 'Delete',
        VIEW: 'View',
        DOWNLOAD: 'Download',
        UPLOAD: 'Upload',
        EXPORT: 'Export',
        IMPORT: 'Import',
        REFRESH: 'Refresh',
        SEARCH: 'Search',
        FILTER: 'Filter',
        SORT: 'Sort',
        CREATE_PROPOSAL: 'Create Proposal',
        EDIT_PROPOSAL: 'Edit Proposal',
        CREATE_LEAD: 'Create Lead',
        CONTACT_BOOK: 'Contact Book',
        VIEW_PROPOSAL: 'Review Proposal',
        VIEW_PROPOSALS: 'View Proposals',
        VIEW_ALL: 'View All',
        VIEW_MORE: 'View More',
        VIEW_LESS: 'View Less',
    },

    // View mode labels
    VIEW_MODES: {
        KANBAN: 'Kanban View',
        TABLE: 'Table View',
    },

    // Footer specific buttons
    FOOTER: {
        REQUEST_CALL_BACK: 'Request Call Back',
        ACCEPT_PROPOSAL: 'Accept Proposal',
    },

    /** School-site add user (ID creation) dialogs — primary actions and loading label */
    ID_CREATION: {
        ADD_TEACHER: 'Add Teacher',
        ADD_ADMIN: 'Add Admin',
        ADD_STUDENT: 'Add Student',
        ADDING: 'Adding...',
    },
} as const

/**
 * Default dialog text configurations
 */
export const DEFAULT_DIALOG_TEXTS = {
    CONFIRMATION: {
        confirmText: BUTTON_TEXT.DIALOG.CONFIRM,
        cancelText: BUTTON_TEXT.DIALOG.CANCEL,
    },
    SUCCESS: {
        confirmText: BUTTON_TEXT.DIALOG.OK,
    },
    ERROR: {
        confirmText: BUTTON_TEXT.DIALOG.OK,
    },
    INFO: {
        confirmText: BUTTON_TEXT.DIALOG.OK,
    },
} as const

/**
 * Validation patterns and rules
 */
export const VALIDATION_PATTERNS = {
    SPECIAL_CHARACTERS: /[!@#$%^&*()_+-=[\]{};':"\\|,.<>/?~`]/,
} as const

/**
 * Form validation error messages
 */
export const VALIDATION_ERROR_MESSAGES = {
    REASSIGNMENT_REASON: {
        required: 'Reason for reassignment is required.',
        minlength: 'Reason must be at least 10 characters long.',
        specialCharacters:
            'Reason cannot contain special characters. Please use only letters, numbers, and spaces.',
    },
    COMMON: {
        required: 'This field is required.',
        minlength: 'This field must be at least {minLength} characters long.',
        maxlength: 'This field cannot exceed {maxLength} characters.',
        email: 'Please enter a valid email address.',
        pattern: 'Please enter a valid format.',
    },
} as const

/**
 * Shared form control error messages used across modules
 */
export const FORM_CONTROL_ERROR_MESSAGES = {
    DEFAULT_LABEL: 'Field',
    EMAIL: 'Please enter a valid email address',
    PHONE: 'Please enter a valid phone number',
    URL: 'Please enter a valid URL',
    DUPLICATE_GRADE: 'Duplicate grade is not allowed',
    HIGH_AMOUNT: 'Amount exceeds ₹1,000,000',
    FUTURE_TIME_NOT_ALLOWED: 'Future time is not allowed for current date',
    PAST_TIME_NOT_ALLOWED: 'Past time is not allowed for current date',
    FUTURE_DATE_NOT_ALLOWED: 'Future dates are not allowed',
    INVALID_DATE: 'Please enter a valid date',
} as const

/**
 * Client and engagement type constants
 */
export const CLIENT_TYPES = {
    ORGANISATION: 1,
    FUNDER: 2,
} as const

export const ENGAGEMENT_TYPES = {
    NEW: 1,
    ONE_YEAR: 2,
    RETENTION: 3,
} as const

export const CLIENT_TYPE_NAMES = {
    [CLIENT_TYPES.ORGANISATION]: 'Customer',
    [CLIENT_TYPES.FUNDER]: 'Funder',
} as const

export const ENGAGEMENT_TYPE_NAMES = {
    [ENGAGEMENT_TYPES.NEW]: 'New',
    [ENGAGEMENT_TYPES.ONE_YEAR]: 'One Year',
    [ENGAGEMENT_TYPES.RETENTION]: 'Retention',
} as const
